import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Flag, Plus, PlusCircle, Trash2, Target as TargetIcon, Sparkles, X, CheckCircle2, ListChecks } from "lucide-react";

const statusColors = {
  pending: { color: "#F59E0B", bg: "rgba(245,158,11,0.15)" },
  in_progress: { color: "#3B82F6", bg: "rgba(59,130,246,0.15)" },
  achieved: { color: "#059669", bg: "rgba(5,150,105,0.15)" },
};

const priorityColors = {
  low: "text-slate-400",
  medium: "text-[#C9A24B]",
  high: "text-rose-400",
};

const Goals = () => {
  const { receptionists, user } = useOutletContext();
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const [dateFrom, setDateFrom] = useState(first.toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0, 10));
  const [empFilter, setEmpFilter] = useState("all");
  const [data, setData] = useState({ custom_goals: [], objectives: [] });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    target_date: "",
    priority: "medium",
    status: "pending",
    employee_id: "",
  });

  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [activeObjective, setActiveObjective] = useState(null);
  const [noteText, setNoteText] = useState("");

  const [pointDrafts, setPointDrafts] = useState({});

  const load = () => {
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (empFilter !== "all") params.append("employee_id", empFilter);
    api.get(`/goals?${params.toString()}`).then((r) => setData(r.data));
  };
  useEffect(load, [dateFrom, dateTo, empFilter]);

  const createGoal = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      await api.post("/goals", form);
      toast.success("Goal created");
      setOpen(false);
      setForm({ title: "", description: "", target_date: "", priority: "medium", status: "pending", employee_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const changeStatus = async (id, status) => {
    await api.put(`/goals/${id}`, { status });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    await api.delete(`/goals/${id}`);
    toast.success("Deleted");
    load();
  };

  const addPoint = async (goalId) => {
    const text = (pointDrafts[goalId] || "").trim();
    if (!text) return;
    try {
      await api.post(`/goals/${goalId}/points`, { text });
      setPointDrafts((d) => ({ ...d, [goalId]: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitObjectiveNote = async () => {
    const text = noteText.trim();
    if (!text || !activeObjective) return;
    try {
      await api.post("/goals", {
        title: `Note on Objective #${activeObjective.num}: ${activeObjective.goal}`,
        description: text,
        objective_num: activeObjective.num,
        employee_id: empFilter === "all" ? "" : empFilter,
      });
      toast.success("Note added to objective");
      setNoteDialogOpen(false);
      setNoteText("");
      setActiveObjective(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const recName = (id) => receptionists.find((r) => r.employee_id === id)?.name || id;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Team Aspirations</div>
          <h1 className="font-heading text-4xl text-white tracking-tight mt-1 flex items-center gap-3">
            Goals <Flag className="text-[#C9A24B]" size={30} />
          </h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            The 20 automatic Reception Objectives are shown below with room for your own notes.
            Add custom personal goals and drop in extra bullet points anytime.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-goal-btn"
              className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium h-11 px-5"
            >
              <Plus size={16} className="mr-2" /> New Custom Goal
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Feed a New Goal</DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                Add a personal or team goal. You can attach it to a specific receptionist and keep adding bullet points.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <F label="Title">
                <Input
                  data-testid="goal-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                  placeholder="e.g. Hit 50 Google reviews this month"
                />
              </F>
              <F label="Description">
                <Textarea
                  data-testid="goal-description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Target Date">
                  <Input
                    type="date"
                    data-testid="goal-target-date"
                    value={form.target_date}
                    onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                    className="bg-[#0F172A] border-[#334155] text-white"
                  />
                </F>
                <F label="Priority">
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="goal-priority" className="bg-[#0F172A] border-[#334155] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4} className="bg-[#1E293B] border-[#334155] text-white z-[100]">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
              </div>
              <F label="Attach to Receptionist (optional)">
                <Select value={form.employee_id || "none"} onValueChange={(v) => setForm({ ...form, employee_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="goal-emp" className="bg-[#0F172A] border-[#334155] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="bg-[#1E293B] border-[#334155] text-white z-[100]">
                    <SelectItem value="none">None (team-wide)</SelectItem>
                    {receptionists.map((r) => (
                      <SelectItem key={r.employee_id} value={r.employee_id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </F>
              <Button
                onClick={createGoal}
                data-testid="goal-save-btn"
                className="w-full bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium"
              >
                Create Goal
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="bg-[#1E293B] border-[#334155] p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <F label="From">
            <Input
              type="date"
              data-testid="goals-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-[#0F172A] border-[#334155] text-white"
            />
          </F>
          <F label="To">
            <Input
              type="date"
              data-testid="goals-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-[#0F172A] border-[#334155] text-white"
            />
          </F>
          <F label={`Receptionist (${receptionists.length} loaded)`}>
            <Select value={empFilter} onValueChange={setEmpFilter}>
              <SelectTrigger data-testid="goals-emp-filter" className="bg-[#0F172A] border-[#334155] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4} className="bg-[#1E293B] border-[#334155] text-white z-[100]">
                <SelectItem value="all">All receptionists</SelectItem>
                {receptionists.map((r) => (
                  <SelectItem key={r.employee_id} value={r.employee_id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
        </div>
      </Card>

      {/* Custom Goals */}
      <Card className="bg-[#1E293B] border-[#334155] p-6">
        <div className="flex items-center gap-2 mb-5">
          <TargetIcon size={18} className="text-[#C9A24B]" />
          <h3 className="font-heading text-xl text-white">Custom Goals</h3>
          <Badge className="ml-2 bg-slate-800 border border-slate-600 text-slate-300 font-mono text-[10px]">
            {data.custom_goals.length}
          </Badge>
        </div>
        {data.custom_goals.length === 0 ? (
          <div className="text-slate-500 text-sm italic p-6 text-center border border-dashed border-[#334155] rounded-lg">
            No custom goals in this date range. Click <span className="text-[#C9A24B]">New Custom Goal</span> to add one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.custom_goals.map((g) => {
              const sc = statusColors[g.status] || statusColors.pending;
              return (
                <div
                  key={g.goal_id}
                  data-testid={`custom-goal-${g.goal_id}`}
                  className="p-4 rounded-lg bg-[#0F172A] border border-[#334155] hover:border-[#475569] transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className={`text-[10px] uppercase font-mono tracking-widest ${priorityColors[g.priority]}`}>
                        {g.priority} priority
                      </div>
                      <div className="font-heading text-white text-lg mt-1 leading-snug break-words">{g.title}</div>
                    </div>
                    <button
                      onClick={() => remove(g.goal_id)}
                      data-testid={`goal-delete-${g.goal_id}`}
                      className="text-slate-500 hover:text-rose-400 transition-colors shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {g.description && <div className="text-sm text-slate-400 mt-2 leading-relaxed">{g.description}</div>}
                  <div className="flex items-center gap-2 mt-3 text-[10px] font-mono text-slate-500">
                    {g.target_date && <span>🎯 {g.target_date}</span>}
                    {g.employee_id && <span>· {recName(g.employee_id)}</span>}
                    <span>· by {g.created_by_email}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Select value={g.status} onValueChange={(v) => changeStatus(g.goal_id, v)}>
                      <SelectTrigger
                        data-testid={`goal-status-${g.goal_id}`}
                        className="h-8 text-xs bg-[#1E293B] border-[#334155] w-36"
                        style={{ color: sc.color }}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={4} className="bg-[#1E293B] border-[#334155] text-white z-[100]">
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="achieved">Achieved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Points */}
                  {g.points?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#334155] space-y-1.5">
                      {g.points.map((p, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-slate-300 leading-relaxed">
                          <span className="text-[#C9A24B] mt-1">•</span>
                          <span className="flex-1">{p.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Input
                      value={pointDrafts[g.goal_id] || ""}
                      onChange={(e) => setPointDrafts((d) => ({ ...d, [g.goal_id]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && addPoint(g.goal_id)}
                      placeholder="Add another point…"
                      data-testid={`point-input-${g.goal_id}`}
                      className="h-8 text-xs bg-[#1E293B] border-[#334155] text-white"
                    />
                    <Button
                      onClick={() => addPoint(g.goal_id)}
                      data-testid={`point-add-${g.goal_id}`}
                      className="h-8 px-3 bg-[#1E293B] hover:bg-[#334155] text-[#C9A24B] border border-[#334155]"
                    >
                      <PlusCircle size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Objectives with notes */}
      <Card className="bg-[#1E293B] border-[#334155] p-6">
        <div className="flex items-center gap-2 mb-5">
          <ListChecks size={18} className="text-[#C9A24B]" />
          <h3 className="font-heading text-xl text-white">20 Reception Objectives · Automatic Linked Goals</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.objectives.map((o) => (
            <div
              key={o.num}
              data-testid={`objective-${o.num}`}
              className="p-4 rounded-lg bg-[#0F172A] border border-[#334155] hover:border-[#C9A24B]/60 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[#C9A24B] font-mono text-xs">#{String(o.num).padStart(2, "0")}</div>
                  <div className="font-heading text-white text-lg mt-1 leading-snug">{o.goal}</div>
                  <div className="text-xs text-slate-500 mt-2 leading-relaxed">{o.day_to_day}</div>
                  <div className="mt-2 text-[10px] uppercase tracking-widest font-mono text-emerald-400/80">{o.kpi}</div>
                </div>
                <button
                  onClick={() => { setActiveObjective(o); setNoteDialogOpen(true); }}
                  data-testid={`objective-add-note-${o.num}`}
                  className="shrink-0 text-slate-400 hover:text-[#C9A24B] transition-colors"
                  title="Add note to this objective"
                >
                  <PlusCircle size={16} />
                </button>
              </div>
              {o.notes.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#334155] space-y-2">
                  {o.notes.map((n) => (
                    <div key={n.goal_id} className="flex items-start gap-2">
                      <CheckCircle2 size={12} className="text-emerald-400 mt-1 shrink-0" />
                      <div className="flex-1 text-xs text-slate-300 leading-relaxed">
                        <div>{n.description || n.title}</div>
                        <div className="text-[10px] font-mono text-slate-500 mt-1">
                          {n.created_by_email} · {new Date(n.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => remove(n.goal_id)}
                        data-testid={`objective-note-delete-${n.goal_id}`}
                        className="text-slate-600 hover:text-rose-400 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Objective note dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles size={18} className="text-[#C9A24B]" />
              Add note to Objective #{activeObjective?.num}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              {activeObjective?.goal}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="objective-note-text"
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="e.g. Introduce hand-massage while patients wait — improves premium feel"
            className="bg-[#0F172A] border-[#334155] text-white"
          />
          <Button
            onClick={submitObjectiveNote}
            data-testid="objective-note-save"
            className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium"
          >
            Save Note
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const F = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">{label}</Label>
    {children}
  </div>
);

export default Goals;
